/**
 * @file   src/helpers/priceCapUtils.js
 * @brief  Price formatting and demand analysis utilities for Match tab
 */
import {
  BUY,
  LONG_RENT,
} from "../constants/common";

/**
 * Format a price into a compact human-readable string.
 * Examples: 500000 -> "$500K", 1200000 -> "$1.2M", 750 -> "$750"
 *
 * @param {number} value - The price value to format
 * @returns {string} Compact price string with $ prefix
 */
export const formatCompactPrice = (value) => {
  if (value == null || isNaN(value)) return "$0";
  const num = Math.abs(value);
  if (num >= 1000000) {
    const millions = num / 1000000;
    const formatted = millions % 1 === 0
      ? millions.toFixed(0)
      : millions.toFixed(1);
    return `$${formatted}M`;
  }
  if (num >= 1000) {
    const thousands = num / 1000;
    const formatted = thousands % 1 === 0
      ? thousands.toFixed(0)
      : thousands.toFixed(1);
    return `$${formatted}K`;
  }
  return `$${Math.round(num)}`;
};

/**
 * Find the bin index with the highest count (peak demand).
 *
 * @param {number[]} prices - Array of numeric prices
 * @param {number} numBins - Number of bins to divide into
 * @param {number} max - Maximum price value for bin range
 * @returns {number|null} Index of the peak bin, or null if insufficient data
 */
export const computePeakDemandBin = (prices, numBins, max) => {
  if (!prices || prices.length < 3 || !max || max <= 0 || !numBins) return null;

  const binWidth = max / numBins;
  const bins = new Array(numBins).fill(0);

  prices.forEach((price) => {
    let idx = Math.floor(price / binWidth);
    if (idx >= numBins) idx = numBins - 1;
    if (idx < 0) idx = 0;
    bins[idx]++;
  });

  let peakIdx = 0;
  for (let i = 1; i < numBins; i++) {
    if (bins[i] > bins[peakIdx]) peakIdx = i;
  }

  return bins[peakIdx] > 0 ? peakIdx : null;
};

/**
 * Compute the price range where most buyer demand concentrates.
 * Returns the range covering the middle 60% of price data.
 *
 * @param {number[]} prices - Array of numeric prices
 * @param {string} adType - BUY, LONG_RENT, or SHORT_RENT
 * @returns {{ min: number, max: number }|null} Sweet spot range or null
 */
export const computeSweetSpotRange = (prices, adType) => {
  if (!prices || prices.length < 3) return null;

  const sorted = [...prices].sort((a, b) => a - b);

  // Middle 60% - from P20 to P80
  const lowIdx = Math.floor(sorted.length * 0.2);
  const highIdx = Math.floor(sorted.length * 0.8);
  const rawMin = sorted[lowIdx];
  const rawMax = sorted[Math.min(highIdx, sorted.length - 1)];

  // Round to nice numbers based on ad type
  let roundTo;
  if (adType === BUY) {
    roundTo = rawMin < 500000 ? 25000 : 50000;
  } else if (adType === LONG_RENT) {
    roundTo = 100;
  } else {
    roundTo = 50;
  }

  const min = Math.floor(rawMin / roundTo) * roundTo;
  const max = Math.ceil(rawMax / roundTo) * roundTo;

  return { min, max };
};

/**
 * Calculate slider range centered on a property's asking price with tiered padding.
 * Used on the Match tab when a property is selected.
 *
 * @param {number} price - The property's asking price
 * @param {string} adType - BUY, LONG_RENT, or SHORT_RENT
 * @returns {{ min: number, max: number, step: number }|null}
 */
export const calculatePropertyPriceRange = (price, adType) => {
  if (!price || price <= 0) return null;

  let padding, step;

  if (adType === BUY) {
    if (price < 300000) {
      padding = 50000;
      step = 5000;
    } else if (price < 1000000) {
      padding = 100000;
      step = 10000;
    } else if (price < 3000000) {
      padding = 500000;
      step = 25000;
    } else {
      padding = 1000000;
      step = 50000;
    }
  } else if (adType === LONG_RENT) {
    if (price < 2000) {
      padding = 500;
      step = 50;
    } else if (price < 5000) {
      padding = 1000;
      step = 100;
    } else {
      padding = 2000;
      step = 200;
    }
  } else {
    // STR (nightly)
    if (price < 200) {
      padding = 50;
      step = 10;
    } else if (price < 500) {
      padding = 100;
      step = 25;
    } else {
      padding = 200;
      step = 50;
    }
  }

  const rangeMin = Math.max(0, Math.floor((price - padding) / step) * step);
  const rangeMax = Math.ceil((price + padding) / step) * step;

  return { min: rangeMin, max: rangeMax, step };
};
